/* ServicesHeader — dark Apple-clean header matching the SystemsHeader
 * pattern. Mounted on every Services page (/services, /services/p/:slug,
 * /services/checkout-success) and on the cart-central storefront (/store/*)
 * so all Services surfaces share one visual language with Systems/Academy.
 *
 * Structure (identical to SystemsHeader):
 *   1. Top utility strip (region · currency · language · search)
 *   2. Dark nav bar:
 *        Logo · Home · Shop ▾ · Deals · Support · Resources ▾
 *        Switch-division pill · Book Consultation · Shop Now CTA
 *   3. Mega-menu panel (hover-open) with featured visual + 3-col text links
 *   4. Mobile accordion drawer
 */
import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import {
  Home, ChevronDown, ChevronLeft, Search, Menu, X, ArrowRight, Phone,
  AlignJustify, Lock, Settings, Wrench, MonitorSmartphone, Settings2, MessageCircle,
  ShieldCheck, Database, Camera, Cloud, Wifi, Globe, LayoutGrid, Key,
  Monitor, Box, Activity, BarChart3, Headphones, LifeBuoy, Workflow, Share2,
  BookOpen, FileText, Library,
  Laptop, Smartphone, Network, Cable, BellRing, Server, HardDrive, Package,
  ShoppingCart, ShoppingBag, Tag, Percent, Truck, RotateCcw, CreditCard, Inbox,
} from 'lucide-react';
import LanguageSelector from './LanguageSelector';
import CurrencySelector from './CurrencySelector';
import RegionSelector from './RegionSelector';
import ZheltaLogo from './ZheltaLogo';
import { trackNavClick } from '../utils/trackNavClick';

const ICONS = {
  AlignJustify, Lock, Settings, Wrench, MonitorSmartphone, Settings2, MessageCircle,
  ShieldCheck, Database, Camera, Cloud, Wifi, Globe, LayoutGrid, Key,
  Monitor, Box, Activity, BarChart3, Headphones, LifeBuoy, Workflow, Share2,
  BookOpen, FileText, Library,
  Laptop, Smartphone, Network, Cable, BellRing, Server, HardDrive, Package,
  ShoppingCart, ShoppingBag, Tag, Percent, Truck, RotateCcw, CreditCard, Inbox,
};

// Services-specific menu data — mirrors the SYSTEMS_MENU shape so we can
// reuse the same MegaMenu component wholesale.
//
// EVERY category link below is backed by a non-empty category slug that
// exists in the live catalog (verified against /api/store/products). If
// you rename a category in Mongo, update this map so no menu item leads
// to an empty page.
const SERVICES_MENU = {
  shop: {
    label: 'Shop',
    href: '/services',
    groups: [
      {
        heading: 'COMPUTING',
        icon: 'Monitor',
        items: [
          { label: 'Laptops',               icon: 'Laptop',             href: '/store/products?category=laptops' },
          { label: 'Desktops',              icon: 'MonitorSmartphone',  href: '/store/products?category=desktops' },
          { label: 'Monitors & displays',   icon: 'Monitor',            href: '/store/products?category=monitors' },
          { label: 'Tablets',               icon: 'Smartphone',         href: '/store/products?category=tablets' },
          { label: 'Phones',                icon: 'Smartphone',         href: '/store/products?category=phones' },
        ],
      },
      {
        heading: 'SECURITY & ACCESSORIES',
        icon: 'ShieldCheck',
        items: [
          { label: 'Security cameras',      icon: 'Camera',             href: '/store/products?category=security-cameras' },
          { label: 'Accessories',           icon: 'Cable',              href: '/store/products?category=accessories' },
        ],
      },
      {
        heading: 'SOFTWARE',
        icon: 'Package',
        items: [
          { label: 'Software & licenses',   icon: 'Package',            href: '/store/products?category=software' },
        ],
      },
    ],
    featured: {
      label: 'MARKETPLACE',
      title: 'ZHELTA Marketplace',
      body: 'Shop verified IT hardware, accessories, and licenses with worldwide delivery.',
      cta: 'Shop All Products',
      ctaHref: '/services',
    },
    footer: [
      { label: 'Bulk orders',    href: '/store/products' },
      { label: 'Vendor sign-up', href: 'mailto:sales@zhelta.com?subject=Vendor%20sign-up' },
      { label: 'Talk to sales',  href: '/systems/contact' },
    ],
  },
  deals: {
    label: 'Deals',
    href: '/store/products?sort=deals',
    groups: [
      {
        heading: 'TRENDING',
        icon: 'Tag',
        items: [
          { label: 'Today\'s deals',          icon: 'Percent',             href: '/store/products?sort=deals' },
          { label: 'Phone deals',             icon: 'Smartphone',          href: '/store/products?sort=deals&category=phones' },
          { label: 'Laptop deals',            icon: 'Laptop',              href: '/store/products?sort=deals&category=laptops' },
          { label: 'Software deals',          icon: 'Package',             href: '/store/products?sort=deals&category=software' },
        ],
      },
      {
        heading: 'TOP BRANDS',
        icon: 'ShoppingBag',
        items: [
          { label: 'Apple',                   icon: 'Monitor',             href: '/store/products?brand=apple' },
          { label: 'Samsung',                 icon: 'Smartphone',          href: '/store/products?brand=samsung' },
          { label: 'Microsoft',               icon: 'Package',             href: '/store/products?brand=microsoft' },
        ],
      },
      {
        heading: 'MORE BRANDS',
        icon: 'Laptop',
        items: [
          { label: 'Dell',                    icon: 'Laptop',              href: '/store/products?brand=dell' },
          { label: 'HP',                      icon: 'Laptop',              href: '/store/products?brand=hp' },
          { label: 'Lenovo',                  icon: 'Laptop',              href: '/store/products?brand=lenovo' },
          { label: 'OnePlus',                 icon: 'Smartphone',          href: '/store/products?brand=oneplus' },
        ],
      },
    ],
    featured: {
      label: 'SAVINGS',
      title: 'Curated weekly deals',
      body: 'Refreshed every Monday with manufacturer-backed price drops and limited-time bundles.',
      cta: 'See today\'s deals',
      ctaHref: '/store/products?sort=deals',
    },
    footer: [
      { label: 'Refer a friend',  href: '/store/thank-referrer' },
      { label: 'Contact sales',   href: '/systems/contact' },
    ],
  },
  support: {
    label: 'Support',
    href: '/store/shipping',
    groups: [
      {
        heading: 'ORDERS & SHIPPING',
        icon: 'Truck',
        items: [
          { label: 'Shipping & delivery',   icon: 'Truck',               href: '/store/shipping' },
          { label: 'Order tracking',         icon: 'Inbox',               href: '/store/orders' },
          { label: 'International orders',   icon: 'Globe',               href: '/store/shipping' },
        ],
      },
      {
        heading: 'AFTER-SALE',
        icon: 'RotateCcw',
        items: [
          { label: 'Returns & refunds',     icon: 'RotateCcw',           href: '/store/returns' },
          { label: 'Warranty',               icon: 'ShieldCheck',         href: '/store/warranty' },
          { label: 'On-device setup',        icon: 'Wrench',              href: '/store/warranty#setup' },
        ],
      },
      {
        heading: 'ACCOUNT',
        icon: 'CreditCard',
        items: [
          { label: 'My account',            icon: 'Key',                 href: '/store/account' },
          { label: 'Payment options',        icon: 'CreditCard',          href: '/store/account#payments' },
          { label: 'Contact us',             icon: 'MessageCircle',       href: '/services/contact' },
        ],
      },
    ],
    featured: {
      label: '24/7 SUPPORT',
      title: 'Post-purchase support by ZHELTA technicians',
      body: 'Every ZHELTA Services order includes free lifetime technical support — chat, email, or phone.',
      cta: 'Reach out',
      ctaHref: '/services/contact',
    },
    footer: [
      { label: 'Help center',   href: '/support' },
      { label: 'Contact us',    href: '/services/contact' },
    ],
  },
  resources: {
    label: 'Resources',
    href: '/store/guides',
    groups: [
      {
        heading: 'LEARN',
        icon: 'BookOpen',
        items: [
          { label: 'Buyer\'s guides',       icon: 'BookOpen',             href: '/store/guides?tag=guides' },
          { label: 'Product comparisons',   icon: 'BarChart3',           href: '/store/guides?tag=comparisons' },
          { label: 'News & releases',       icon: 'FileText',            href: '/store/guides' },
        ],
      },
      {
        heading: 'BUSINESS',
        icon: 'Workflow',
        items: [
          { label: 'For enterprise',        icon: 'ShoppingBag',         href: '/systems/contact' },
          { label: 'For schools',           icon: 'Library',             href: '/systems/contact' },
          { label: 'For NGOs',              icon: 'Share2',              href: '/systems/contact' },
        ],
      },
    ],
    featured: {
      label: 'GUIDES & RESOURCES',
      title: 'Independent reviews, comparisons & how-tos',
      body: 'Dive into vendor-neutral buyer guides, hands-on product comparisons, and setup walk-throughs written by our engineers.',
      cta: 'Browse all guides',
      ctaHref: '/store/guides',
    },
    footer: [
      { label: 'All guides',      href: '/store/guides' },
      { label: 'Vendor partners', href: '/about' },
    ],
  },
};

// Lead icon for each menu's featured visual (left column of mega panel)
const LEAD_ICON = {
  shop:      'ShoppingBag',
  deals:     'Percent',
  support:   'LifeBuoy',
  resources: 'BookOpen',
};

function MegaMenu({ menuKey, menu, onNavigate }) {
  const groups = menu.groups || [];
  const f = menu.featured || {};
  const ping = (slug, label) => trackNavClick({ slug: `services:${menuKey}:${slug}`, label });
  const LeadIcon = ICONS[LEAD_ICON[menuKey] || 'ShoppingBag'] || ShoppingBag;
  const groupColCls = groups.length <= 2 ? 'grid-cols-2' : 'grid-cols-2 lg:grid-cols-3';

  return (
    <div className="rounded-2xl bg-white shadow-[0_16px_50px_rgba(0,0,0,0.08)] border border-zinc-100 overflow-hidden">
      <div className="grid grid-cols-12 gap-8 p-8">
        {/* LEFT — featured visual + tagline */}
        <div className="col-span-4">
          <div
            className="relative rounded-2xl overflow-hidden h-[260px] flex items-center justify-center"
            style={{ background: 'linear-gradient(135deg, #fff8e7 0%, #fef3c7 50%, #fde68a 100%)' }}
          >
            <div
              className="absolute inset-0 opacity-25"
              style={{
                background:
                  'radial-gradient(circle at 30% 30%, rgba(245,166,35,0.55) 0%, transparent 55%), radial-gradient(circle at 75% 70%, rgba(255,140,26,0.35) 0%, transparent 55%)',
              }}
            />
            <LeadIcon className="relative w-28 h-28 text-[#8a6710]" strokeWidth={1.4} />
          </div>
          <h4 className="text-[20px] font-extrabold text-gray-900 mt-5 mb-1.5 leading-tight">{f.title}</h4>
          <p className="text-[13px] text-gray-500 leading-6 mb-3">{f.body}</p>
          <Link
            to={f.ctaHref || '/services'}
            onClick={() => { ping('featured-cta', f.cta); onNavigate && onNavigate(); }}
            data-testid={`services-nav-featured-cta-${(f.cta || '').toLowerCase().replace(/[^a-z0-9]+/g, '-')}`}
            className="inline-flex items-center gap-1 text-[14px] font-semibold text-[#c9a227] hover:underline"
          >
            {f.cta || 'Learn more'} <ArrowRight className="w-4 h-4" />
          </Link>
        </div>

        {/* RIGHT — text-link columns */}
        <div className={`col-span-8 grid ${groupColCls} gap-x-8 gap-y-6`}>
          {groups.map((g) => (
            <div key={g.heading}>
              <span className="block text-[11px] font-bold tracking-[0.22em] text-gray-400 uppercase mb-4">
                {g.heading}
              </span>
              <ul className="space-y-3">
                {g.items.map((it) => (
                  <li key={it.label}>
                    <Link
                      to={it.href}
                      onClick={() => { ping(`item:${it.href}`, it.label); onNavigate && onNavigate(); }}
                      data-testid={`services-nav-item-${it.label.toLowerCase().replace(/[^a-z0-9]+/g, '-')}`}
                      className="text-[15px] text-gray-800 hover:text-[#c9a227] transition-colors"
                    >
                      {it.label}
                    </Link>
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>
      </div>

      <div className="border-t border-gray-100 px-8 py-4 flex items-center justify-between flex-wrap gap-3">
        <Link
          to={menu.href || '/services'}
          onClick={() => { ping('explore-all', `Explore all ${menu.label}`); onNavigate && onNavigate(); }}
          data-testid={`services-nav-explore-all-${menuKey}`}
          className="inline-flex items-center gap-1 text-[14px] font-semibold text-[#c9a227] hover:underline"
        >
          Explore all {menu.label} <ArrowRight className="w-4 h-4" />
        </Link>
        <div className="text-[13px] text-gray-500">
          Need help choosing?{' '}
          <Link
            to="/systems/contact"
            onClick={() => { ping('contact-specialist', 'Contact a specialist'); onNavigate && onNavigate(); }}
            data-testid={`services-nav-specialist-${menuKey}`}
            className="font-semibold text-gray-900 hover:text-[#c9a227] transition-colors"
          >
            Contact a specialist
          </Link>
        </div>
      </div>
    </div>
  );
}

function NavItem({ label, icon: Icon, to = '#', hasMenu, menuKey, isOpen, testId, onOpen, onClose, onToggle }) {
  // Touch-device support: on tablets/phones "hover" doesn't exist, so
  // tapping the nav item must TOGGLE the mega-menu instead of navigating
  // straight to the parent route. We detect a coarse pointer at the
  // point of click — desktops with a mouse keep the classic
  // hover-to-open + click-to-navigate behaviour.
  const handleClick = (e) => {
    if (!hasMenu) return; // leaf nav item — let the Link navigate normally
    const isCoarse = typeof window !== 'undefined' &&
      window.matchMedia && window.matchMedia('(pointer: coarse)').matches;
    if (isCoarse) {
      // First tap on a coarse pointer → open the mega-menu.
      // Second tap on the same (already-open) item → navigate to the parent.
      if (!isOpen) {
        e.preventDefault();
        onToggle && onToggle(menuKey);
      }
    }
    trackNavClick({ slug: `services:${menuKey || 'top'}:nav`, label });
  };
  return (
    <div
      className="relative"
      onMouseEnter={() => hasMenu && onOpen(menuKey)}
      onMouseLeave={() => hasMenu && onClose()}
    >
      <Link
        to={to}
        data-testid={testId}
        onClick={handleClick}
        aria-expanded={hasMenu ? isOpen : undefined}
        aria-haspopup={hasMenu ? 'menu' : undefined}
        className={`flex items-center gap-1.5 px-4 py-2 text-[13.5px] font-semibold transition-colors ${
          isOpen ? 'text-zinc-900' : 'text-zinc-700 hover:text-zinc-900'
        }`}
      >
        {Icon && <Icon className="w-4 h-4" />}
        <span>{label}</span>
        {hasMenu && (
          <ChevronDown
            className={`w-3.5 h-3.5 transition-transform ${isOpen ? 'rotate-180 text-zinc-900' : 'text-zinc-400'}`}
            strokeWidth={2.5}
          />
        )}
      </Link>
    </div>
  );
}

export default function ServicesHeader() {
  const navigate = useNavigate();
  const [mobileOpen, setMobileOpen] = useState(false);
  const [expanded, setExpanded] = useState(null);
  const [searchValue, setSearchValue] = useState('');
  const [openKey, setOpenKey] = useState(null);
  const closeTimer = React.useRef(null);
  const closeMobile = () => setMobileOpen(false);

  const handleSearch = (e) => {
    e.preventDefault();
    const q = searchValue.trim();
    trackNavClick({ slug: 'services:search:submit', label: q || 'empty' });
    navigate(q ? `/store/products?q=${encodeURIComponent(q)}` : '/store/products');
    setMobileOpen(false);
  };

  const openMenu = (key) => {
    if (closeTimer.current) { clearTimeout(closeTimer.current); closeTimer.current = null; }
    setOpenKey(key);
  };
  const scheduleClose = () => {
    if (closeTimer.current) clearTimeout(closeTimer.current);
    // 350ms instead of 150ms — small cursor wobbles between the nav item
    // and the panel no longer dismiss the menu prematurely. Also gives
    // touch users enough time to see the panel appear on tap.
    closeTimer.current = setTimeout(() => setOpenKey(null), 350);
  };
  const cancelClose = () => {
    if (closeTimer.current) { clearTimeout(closeTimer.current); closeTimer.current = null; }
  };
  const closeNow = () => { cancelClose(); setOpenKey(null); };
  // Tap-toggle for coarse-pointer devices (iPad / phones): tapping the
  // same top-level nav closes the panel; tapping a different one swaps.
  const toggleMenu = (key) => {
    if (closeTimer.current) { clearTimeout(closeTimer.current); closeTimer.current = null; }
    setOpenKey((prev) => (prev === key ? null : key));
  };

  // Outside-click handler — essential on touch because there's no
  // "mouseleave" to dismiss an open mega-menu. Tapping anywhere outside
  // the nav closes it cleanly.
  React.useEffect(() => {
    if (!openKey) return undefined;
    const onDocClick = (e) => {
      const nav = e.target.closest && e.target.closest('[data-mega-nav="services"]');
      if (!nav) closeNow();
    };
    document.addEventListener('click', onDocClick);
    return () => document.removeEventListener('click', onDocClick);
  }, [openKey]);  // eslint-disable-line react-hooks/exhaustive-deps

  const openMenuObj = openKey ? SERVICES_MENU[openKey] : null;

  return (
    <header className="sticky top-0 z-50 w-full shadow-sm" data-testid="services-header">
      {/* Top utility bar — matches Systems */}
      <div className="bg-white border-b border-zinc-100">
        <div className="zh-container flex items-center justify-between gap-3 py-2 flex-wrap">
          {/* Left: persistent "back to main site" link */}
          <Link
            to="/"
            data-testid="services-back-to-main"
            className="inline-flex items-center gap-1 text-[12px] font-semibold text-zinc-500 hover:text-[#c9a227] transition-colors"
            title="Back to ZHELTA main site"
          >
            <ChevronLeft className="w-3.5 h-3.5" strokeWidth={2.5} />
            ZHELTA
          </Link>
          <div className="flex items-center gap-3 flex-wrap">
            <RegionSelector />
            <CurrencySelector />
            <LanguageSelector />
            <form
              onSubmit={handleSearch}
              role="search"
              data-testid="services-search-form"
              className="relative flex items-center w-full sm:w-auto"
            >
              <input
                type="text"
                value={searchValue}
                onChange={(e) => setSearchValue(e.target.value)}
                placeholder="Search products, brands, guides…"
                data-testid="services-search-input"
                aria-label="Search products, brands, guides"
                className="w-full sm:w-56 md:w-72 pl-4 pr-10 py-1.5 rounded-full border border-amber-200/80 bg-white focus:outline-none focus:border-amber-400 text-sm transition-colors"
              />
              <button
                type="submit"
                aria-label="Search"
                data-testid="services-search-submit"
                className="absolute right-2 p-1 text-gray-500 hover:text-[#c9a227] transition-colors"
              >
                <Search className="w-4 h-4" />
              </button>
            </form>
          </div>
        </div>
      </div>

      {/* Main nav bar — DARK, matching /systems */}
      <div className="bg-white text-zinc-900 border-b border-zinc-200 relative" data-mega-nav="services">
        <div className="zh-container flex items-center h-[78px] gap-6">
          {/* Logo (left) */}
          <Link to="/services" data-testid="services-nav-logo" className="flex items-center gap-2.5 shrink-0">
            <ZheltaLogo size={40} />
            <div className="leading-none">
              <div className="text-[22px] font-extrabold tracking-[0.18em] text-zinc-900">ZHELTA</div>
              <div className="text-[9px] font-semibold tracking-[0.3em] text-[#f5a623] mt-0.5">SERVICES</div>
            </div>
          </Link>

          {/* Desktop nav — CENTERED */}
          <nav className="hidden lg:flex flex-1 items-center justify-center" onMouseLeave={scheduleClose}>
            <NavItem label="Home" icon={Home} to="/services" testId="services-nav-home" menuKey="home" isOpen={false} onOpen={closeNow} onClose={scheduleClose} />
            <NavItem label={SERVICES_MENU.shop.label}      to={SERVICES_MENU.shop.href}      hasMenu menuKey="shop"      isOpen={openKey === 'shop'}      testId="services-nav-shop"      onOpen={openMenu} onClose={scheduleClose} onToggle={toggleMenu} />
            <NavItem label={SERVICES_MENU.deals.label}     to={SERVICES_MENU.deals.href}     hasMenu menuKey="deals"     isOpen={openKey === 'deals'}     testId="services-nav-deals"     onOpen={openMenu} onClose={scheduleClose} onToggle={toggleMenu} />
            <NavItem label={SERVICES_MENU.support.label}   to={SERVICES_MENU.support.href}   hasMenu menuKey="support"   isOpen={openKey === 'support'}   testId="services-nav-support"   onOpen={openMenu} onClose={scheduleClose} onToggle={toggleMenu} />
            <NavItem label={SERVICES_MENU.resources.label} to={SERVICES_MENU.resources.href} hasMenu menuKey="resources" isOpen={openKey === 'resources'} testId="services-nav-resources" onOpen={openMenu} onClose={scheduleClose} onToggle={toggleMenu} />
          </nav>

          {/* Filler so CTAs stay right-aligned on mobile */}
          <div className="lg:hidden flex-1" />

          {/* CTAs (right) — Switch-division pill removed; the horizontal
              DivisionStripBar directly below this header already surfaces
              the other divisions, so the dropdown was redundant. */}
          <div className="hidden lg:flex items-center gap-3 shrink-0">
            <Link
              to="/store/cart"
              data-testid="services-nav-cart"
              onClick={() => trackNavClick({ slug: 'services:cta:cart', label: 'Cart' })}
              className="relative p-2 rounded-full border-2 border-zinc-300 hover:border-zinc-900 hover:text-zinc-900 transition-colors"
              title="View cart"
              aria-label="View cart"
            >
              <ShoppingCart className="w-4 h-4" />
            </Link>
            <a
              href="https://portal.zhelta.com/p/scheduler/N93cR2NRHmrmriKDT"
              target="_blank"
              rel="noopener noreferrer"
              data-testid="services-nav-book-consultation"
              onClick={() => trackNavClick({ slug: 'services:cta:book-consultation', label: 'Book consultation' })}
              className="px-5 py-2.5 rounded-full text-[12px] font-semibold tracking-wider uppercase border-2 border-zinc-300 text-zinc-900 hover:bg-[#f5a623] hover:text-[#0e0e10] hover:border-[#f5a623] transition-colors inline-flex items-center gap-2"
            >
              <Phone className="w-3.5 h-3.5" />
              Book a Consultation
            </a>
            <Link
              to="/services"
              data-testid="services-nav-shop-now"
              onClick={() => trackNavClick({ slug: 'services:cta:shop-now', label: 'Shop now' })}
              className="px-5 py-2.5 rounded-full text-[12px] font-semibold tracking-wider uppercase text-zinc-900 hover:brightness-95 transition-all"
              style={{
                background: 'linear-gradient(90deg, #ffb347 0%, #f5a623 50%, #ff8c1a 100%)',
                boxShadow: '0 10px 20px -8px rgba(245, 140, 25, 0.5)',
              }}
            >
              Shop Now
            </Link>
          </div>

          {/* Mobile menu button */}
          <button
            className="lg:hidden p-2 text-zinc-900"
            onClick={() => setMobileOpen((v) => !v)}
            aria-label="Toggle menu"
            data-testid="services-nav-mobile-toggle"
          >
            {mobileOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
          </button>
        </div>

        {/* Mega panel */}
        {openMenuObj && (
          <div
            className="hidden lg:block absolute left-1/2 -translate-x-1/2 top-full pt-3 w-[min(1240px,96vw)] z-[80]"
            onMouseEnter={cancelClose}
            onMouseLeave={scheduleClose}
            data-testid={`services-nav-mega-${openKey}`}
          >
            <MegaMenu menuKey={openKey} menu={openMenuObj} onNavigate={closeNow} />
          </div>
        )}
      </div>

      {/* Mobile accordion */}
      {mobileOpen && (
        <div className="lg:hidden border-t border-black/20 bg-white text-zinc-900 border-b border-zinc-200 max-h-[calc(100vh-120px)] overflow-y-auto">
          <div className="zh-container py-3 flex flex-col">
            <Link to="/services" onClick={closeMobile} className="flex items-center gap-2 px-3 py-3 text-sm font-semibold text-zinc-900 hover:bg-zinc-100 rounded-md">
              <Home className="w-4 h-4" /> Home
            </Link>
            {Object.entries(SERVICES_MENU).map(([key, m]) => (
              <div key={key}>
                <button
                  onClick={() => setExpanded(expanded === key ? null : key)}
                  className="w-full flex items-center justify-between px-3 py-3 text-sm font-semibold text-zinc-900 hover:bg-zinc-100 rounded-md"
                  data-testid={`services-nav-mobile-toggle-${key}`}
                >
                  <span>{m.label}</span>
                  <ChevronDown className={`w-4 h-4 transition-transform ${expanded === key ? 'rotate-180' : ''}`} />
                </button>
                {expanded === key && (
                  <div className="pl-5 pb-2">
                    {m.groups.map((g) => (
                      <div key={g.heading} className="mb-3">
                        <div className="text-[10px] font-bold tracking-[0.18em] text-zinc-500 mb-1.5 mt-2">{g.heading}</div>
                        <ul className="space-y-1.5">
                          {g.items.map((it) => {
                            const Icon = ICONS[it.icon] || MessageCircle;
                            return (
                              <li key={it.label}>
                                <Link
                                  to={it.href}
                                  onClick={() => {
                                    trackNavClick({ slug: `services:${key}:item:${it.href}`, label: it.label });
                                    closeMobile();
                                  }}
                                  className="flex items-center gap-2 text-[13px] text-zinc-700 py-1 hover:text-[#f5a623]"
                                >
                                  <Icon className="w-3.5 h-3.5 text-zinc-500" />
                                  {it.label}
                                </Link>
                              </li>
                            );
                          })}
                        </ul>
                      </div>
                    ))}
                    {m.footer && m.footer.length > 0 && (
                      <div className="flex flex-wrap gap-x-4 gap-y-1 pt-1 pb-2">
                        {m.footer.map((link) => (
                          <Link
                            key={link.label}
                            to={link.href}
                            onClick={closeMobile}
                            className="text-[12px] text-zinc-500 hover:text-[#f5a623] font-medium"
                          >
                            {link.label}
                          </Link>
                        ))}
                      </div>
                    )}
                  </div>
                )}
              </div>
            ))}
            <div className="flex items-center gap-3 px-3 pt-4 pb-3">
              <a
                href="https://portal.zhelta.com/p/scheduler/N93cR2NRHmrmriKDT"
                target="_blank"
                rel="noopener noreferrer"
                onClick={closeMobile}
                data-testid="services-nav-mobile-book-consultation"
                className="flex-1 text-center px-4 py-2.5 rounded-full text-[13px] font-semibold uppercase text-zinc-900 border-2 border-zinc-300"
              >
                Book a Consultation
              </a>
              <Link
                to="/services"
                onClick={closeMobile}
                className="flex-1 text-center px-4 py-2.5 rounded-full text-[13px] font-semibold uppercase text-white"
                style={{ background: 'linear-gradient(90deg, #ffb347 0%, #f5a623 50%, #ff8c1a 100%)' }}
              >
                Shop Now
              </Link>
            </div>
          </div>
        </div>
      )}
    </header>
  );
}
